import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";
import { Plus, Check, X, Waves, Edit, Trash2, ChevronDown, UserPlus, Settings, Settings2, Calendar, Clock, Eye, EyeOff, Search, ChevronLeft, ChevronRight, Filter, Users, Undo2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { getLessonStatus, getLessonStatusLabel, getLessonStatusColor } from "../../lib/lessonUtils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { API } from "../../App";
import { getCatLabel, RANKING_COLORS } from "./constants";
import LessonQRCode from "../LessonQRCode";
import AdminSchedule from "../../components/admin/AdminSchedule";

const AdminLessons = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [lessonEnrollments, setLessonEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [showAutoConfig, setShowAutoConfig] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [pastPage, setPastPage] = useState(1);
  const [pastPages, setPastPages] = useState(1);
  const [pastTotal, setPastTotal] = useState(0);
  const [pastDateFrom, setPastDateFrom] = useState("");
  const [pastDateTo, setPastDateTo] = useState("");
  const [pastTeacherId, setPastTeacherId] = useState("");
  const [pastCategory, setPastCategory] = useState("");
  const [pastSearch, setPastSearch] = useState("");
  const [pastSearchInput, setPastSearchInput] = useState("");
  const [showPastFilters, setShowPastFilters] = useState(false);
  const pastSearchDebounceRef = useRef(null);
  const PAST_LIMIT = 15;
  const [searchParams, setSearchParams] = useSearchParams();
  const expandRef = useRef(null);
  const [studentList, setStudentList] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  // Add student to lesson
  const [addStudentLessonId, setAddStudentLessonId] = useState(null);
  const [addStudentSearch, setAddStudentSearch] = useState("");
  const [addStudentResults, setAddStudentResults] = useState([]);
  const [addStudentLoading, setAddStudentLoading] = useState(false);
  // Inline student creation in lesson detail
  const [showAddStudentCreate, setShowAddStudentCreate] = useState(false);
  const [addNewStudent, setAddNewStudent] = useState({ name: "", email: "", phone: "" });
  const [formData, setFormData] = useState({
    description: "",
    lesson_type: "group",
    lesson_category: "both",
    date: "",
    time_slot: "morning",
    start_time: "09:00",
    end_time: "11:00",
    max_students: 8,
    price: 25,
    instructor: "",
    teachers: [],
  });

  useEffect(() => {
    fetchLessons();
    fetchStaff();
    fetchStudents();
  }, []);

  // Auto-expand lesson from URL param
  useEffect(() => {
    const expandId = searchParams.get("expand");
    if (expandId && lessons.length > 0 && !expandedLesson) {
      toggleExpand(expandId);
      setSearchParams({}, { replace: true });
      setTimeout(() => {
        if (expandRef.current) expandRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [lessons, searchParams]);

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/staff/list`, { withCredentials: true });
      setStaffList(res.data);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API}/admin/students`, { withCredentials: true });
      // API returns { students: [...], total: ... }
      setStudentList(res.data.students || []);
    } catch (err) {
      console.error("Error fetching students:", err);
    }
  };

  const handleCancelLesson = async (lessonId) => {
    if (!window.confirm("Cancelar esta aula? Todos os créditos serão devolvidos aos alunos inscritos.")) return;
    try {
      const res = await axios.post(`${API}/lessons/${lessonId}/cancel`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchLessons();
      setExpandedLesson(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleMarkAbsent = async (enrollmentId, refund) => {
    try {
      await axios.post(`${API}/enrollments/${enrollmentId}/absent`, { refund }, { withCredentials: true });
      toast.success(refund ? "Falta marcada (crédito devolvido)" : "Falta marcada");
      if (expandedLesson) {
        const res = await axios.get(`${API}/enrollments/lesson/${expandedLesson}`, { withCredentials: true });
        setLessonEnrollments(res.data);
      }
      fetchLessons();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleRevertAttendance = async (enrollmentId) => {
    if (!window.confirm("Reverter a presença deste aluno? O crédito será devolvido se aplicável.")) return;
    try {
      const res = await axios.patch(`${API}/enrollments/${enrollmentId}/revert-attendance`, {}, { withCredentials: true });
      toast.success(res.data.message);
      if (expandedLesson) {
        const enrollRes = await axios.get(`${API}/enrollments/lesson/${expandedLesson}`, { withCredentials: true });
        setLessonEnrollments(enrollRes.data);
      }
      fetchLessons();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reverter presença"); }
  };

  // Search students for the "add to lesson" modal
  useEffect(() => {
    if (!addStudentLessonId) return;
    const timer = setTimeout(async () => {
      if (!addStudentSearch.trim()) { setAddStudentResults([]); return; }
      try {
        const res = await axios.get(`${API}/admin/students?search=${encodeURIComponent(addStudentSearch)}&limit=8`, { withCredentials: true });
        const students = res.data.students || res.data || [];
        // Filter out already enrolled students
        const enrolledIds = new Set(lessonEnrollments.map(e => e.user_id));
        setAddStudentResults(students.filter(s => !enrolledIds.has(s.user_id)));
      } catch { setAddStudentResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [addStudentSearch, addStudentLessonId]);

  const handleAddStudentToLesson = async (studentId) => {
    setAddStudentLoading(true);
    try {
      const res = await axios.post(`${API}/staff/enrollments`, { lesson_id: addStudentLessonId, user_id: studentId }, { withCredentials: true });
      toast.success(res.data.message);
      // Refresh enrollments
      if (expandedLesson) {
        const enrollRes = await axios.get(`${API}/enrollments/lesson/${expandedLesson}`, { withCredentials: true });
        setLessonEnrollments(enrollRes.data);
      }
      fetchLessons();
      setAddStudentSearch("");
      setAddStudentResults([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao adicionar aluno");
    } finally { setAddStudentLoading(false); }
  };

  // Create new student and add to lesson
  const handleCreateAndAddStudentToLesson = async () => {
    if (!addNewStudent.name.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!addStudentLessonId) return;
    setAddStudentLoading(true);
    try {
      // Create student
      const createRes = await axios.post(`${API}/admin/students`, addNewStudent, { withCredentials: true });
      const newStudentId = createRes.data.user_id;
      // Add to lesson
      await axios.post(`${API}/staff/enrollments`, { lesson_id: addStudentLessonId, user_id: newStudentId }, { withCredentials: true });
      toast.success(`Aluno "${addNewStudent.name}" criado e adicionado à aula`);
      // Refresh enrollments
      if (expandedLesson) {
        const enrollRes = await axios.get(`${API}/enrollments/lesson/${expandedLesson}`, { withCredentials: true });
        setLessonEnrollments(enrollRes.data);
      }
      fetchLessons();
      setAddNewStudent({ name: "", email: "", phone: "" });
      setShowAddStudentCreate(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar aluno");
    } finally { setAddStudentLoading(false); }
  };

  const fetchLessons = useCallback(async (overrides = {}) => {
    try {
      const isPast = overrides.showPast ?? showPast;
      if (isPast) {
        const p = overrides.page ?? pastPage;
        const params = new URLSearchParams({ include_past: "true", page: p, limit: PAST_LIMIT });
        const df = overrides.dateFrom ?? pastDateFrom;
        const dt = overrides.dateTo ?? pastDateTo;
        const tid = overrides.teacherId ?? pastTeacherId;
        const cat = overrides.category ?? pastCategory;
        const srch = overrides.search ?? pastSearch;
        if (df) params.set("date_from", df);
        if (dt) params.set("date_to", dt);
        if (tid && tid !== "all_teachers") params.set("teacher_id", tid);
        if (cat && cat !== "all_types") params.set("lesson_type", cat);
        if (srch) params.set("search", srch);
        const response = await axios.get(`${API}/lessons?${params}`, { withCredentials: true });
        const data = response.data;
        setLessons(data.lessons);
        setPastPage(data.page);
        setPastPages(data.pages);
        setPastTotal(data.total);
      } else {
        const response = await axios.get(`${API}/lessons`, { withCredentials: true });
        setLessons(response.data);
      }
    } catch {
      toast.error("Erro ao carregar aulas");
    } finally {
      setLoading(false);
    }
  }, [showPast, pastPage, pastDateFrom, pastDateTo, pastTeacherId, pastCategory, pastSearch]);

  useEffect(() => {
    setLoading(true);
    setExpandedLesson(null);
    setPastPage(1);
    fetchLessons({ showPast, page: 1 });
  }, [showPast]);

  const handlePastSearchChange = (value) => {
    setPastSearchInput(value);
    clearTimeout(pastSearchDebounceRef.current);
    pastSearchDebounceRef.current = setTimeout(() => {
      setPastSearch(value);
      setPastPage(1);
      fetchLessons({ search: value, page: 1 });
    }, 350);
  };

  const handlePastFilterApply = () => {
    setPastPage(1);
    fetchLessons({ page: 1 });
  };

  const handlePastPageChange = (newPage) => {
    if (newPage < 1 || newPage > pastPages) return;
    setExpandedLesson(null);
    fetchLessons({ page: newPage });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearPastFilters = () => {
    setPastDateFrom("");
    setPastDateTo("");
    setPastTeacherId("");
    setPastCategory("");
    setPastSearch("");
    setPastSearchInput("");
    setPastPage(1);
    fetchLessons({ dateFrom: "", dateTo: "", teacherId: "", category: "", search: "", page: 1 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cat = formData.lesson_category;
    const autoTitle = cat === "surf" ? "Surf" : cat === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard";
    const payload = { ...formData, title: autoTitle };
    // Private lessons: remove optional fields
    if (payload.lesson_type === "private") {
      if (!payload.max_students) payload.max_students = 99;
      if (!payload.price) payload.price = 0;
    }
    try {
      let lessonId = editingLesson?.lesson_id;
      if (editingLesson) {
        await axios.patch(`${API}/lessons/${editingLesson.lesson_id}`, payload, { withCredentials: true });
        toast.success("Aula atualizada!");
      } else {
        const res = await axios.post(`${API}/lessons`, payload, { withCredentials: true });
        lessonId = res.data?.lesson_id;
        toast.success("Aula criada!");
      }
      // Enroll selected students for private lessons
      if (payload.lesson_type === "private" && selectedStudents.length > 0 && lessonId) {
        const actType = cat === "bodyboard" ? "bodyboard" : "surf";
        for (const sid of selectedStudents) {
          try {
            await axios.post(`${API}/staff/enrollments`, { lesson_id: lessonId, user_id: sid, activity_type: actType }, { withCredentials: true });
          } catch (err) {
            console.error("Error enrolling student:", err);
          }
        }
      }
      setIsDialogOpen(false);
      setEditingLesson(null);
      resetForm();
      fetchLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar aula");
    }
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    setFormData({
      description: lesson.description || "",
      lesson_type: lesson.lesson_type,
      lesson_category: lesson.lesson_category,
      date: lesson.date,
      time_slot: lesson.time_slot,
      start_time: lesson.start_time,
      end_time: lesson.end_time,
      max_students: lesson.max_students,
      price: lesson.price,
      instructor: lesson.instructor || "",
      teachers: lesson.teachers || [],
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (lessonId) => {
    if (!window.confirm("Tens a certeza que queres eliminar esta aula? Os créditos dos alunos inscritos serão devolvidos.")) return;
    try {
      const res = await axios.delete(`${API}/lessons/${lessonId}`, { withCredentials: true });
      toast.success(res.data.message || "Aula eliminada!");
      fetchLessons();
    } catch (error) {
      toast.error("Erro ao eliminar aula");
    }
  };

  const handleMarkAttendance = async (enrollmentId) => {
    try {
      await axios.patch(`${API}/enrollments/${enrollmentId}`, { status: "attended" }, { withCredentials: true });
      toast.success("Presença confirmada");
      if (expandedLesson) {
        const res = await axios.get(`${API}/enrollments/lesson/${expandedLesson}`, { withCredentials: true });
        setLessonEnrollments(res.data);
      }
      fetchLessons();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao marcar presença"); }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      lesson_type: "group",
      lesson_category: "both",
      date: "",
      time_slot: "morning",
      start_time: "09:00",
      end_time: "11:00",
      max_students: 8,
      price: 25,
      instructor: "",
      teachers: [],
    });
    setSelectedStudents([]);
    setStudentSearch("");
  };

  const toggleExpand = async (lessonId) => {
    if (expandedLesson === lessonId) {
      setExpandedLesson(null);
      return;
    }
    setExpandedLesson(lessonId);
    setLoadingEnrollments(true);
    try {
      const res = await axios.get(`${API}/enrollments/lesson/${lessonId}`, { withCredentials: true });
      setLessonEnrollments(res.data);
    } catch { toast.error("Erro ao carregar inscrições"); }
    finally { setLoadingEnrollments(false); }
    // Fetch forecast for lesson date
    const lesson = lessons.find(l => l.lesson_id === lessonId);
    if (lesson?.date) {
      try {
        const res = await axios.get(`${API}/weather/forecast/${lesson.date}`);
        setForecast(res.data);
      } catch { setForecast(null); }
    }
  };

  const toggleTeacher = (userId) => {
    setFormData(prev => ({
      ...prev,
      teachers: prev.teachers.includes(userId)
        ? prev.teachers.filter(id => id !== userId)
        : [...prev.teachers, userId]
    }));
  };

  const openNewDialog = () => {
    setEditingLesson(null);
    resetForm();
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-[#3d5a63] mb-1">Gestão de Aulas</h1>
          <p className="text-xs md:text-sm text-[#475569]">Adiciona e gere as aulas da escola</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant={showPast ? "default" : "outline"} size="sm" onClick={() => setShowPast(!showPast)} className={`rounded-full text-xs ${showPast ? "bg-[#475569] hover:bg-[#334155]" : ""}`} data-testid="toggle-past-lessons">
            {showPast ? <EyeOff className="mr-1.5 h-3.5 w-3.5" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{showPast ? "Ocultar Passadas" : "Ver Passadas"}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAutoConfig(!showAutoConfig)} className="rounded-full text-xs" data-testid="toggle-auto-config">
            <Settings2 className="mr-1.5 h-3.5 w-3.5" />
            <span className="hidden sm:inline">Aulas Automáticas</span>
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openNewDialog} className="bg-[#6697a4] hover:bg-[#3d5a63] rounded-full text-xs" data-testid="add-lesson-btn">
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Nova Aula
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {editingLesson ? "Editar Aula" : "Nova Aula"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição da aula..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={formData.lesson_type} onValueChange={(v) => setFormData({ ...formData, lesson_type: v })}>
                    <SelectTrigger data-testid="lesson-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="group">Grupo</SelectItem>
                      <SelectItem value="private">Privada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={formData.lesson_category} onValueChange={(v) => setFormData({ ...formData, lesson_category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Surf & Bodyboard</SelectItem>
                      <SelectItem value="surf">Apenas Surf</SelectItem>
                      <SelectItem value="bodyboard">Apenas Bodyboard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="lesson-date-input"
                  />
                </div>
                <div>
                  <Label>Horário</Label>
                  <Select value={formData.time_slot} onValueChange={(v) => setFormData({ ...formData, time_slot: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Manhã</SelectItem>
                      <SelectItem value="afternoon">Tarde</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Hora Início</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Hora Fim</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>
                {formData.lesson_type !== "private" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Máx. Alunos</Label>
                      <Input
                        type="number"
                        value={formData.max_students}
                        onChange={(e) => setFormData({ ...formData, max_students: e.target.value === '' ? '' : parseInt(e.target.value) })}
                        onBlur={(e) => setFormData({ ...formData, max_students: parseInt(e.target.value) || 1 })}
                        min={1}
                        required
                        data-testid="lesson-max-students-input"
                      />
                    </div>
                    <div>
                      <Label>Preço (€)</Label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        onBlur={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                        min={0}
                        step={0.01}
                        required
                        data-testid="lesson-price-input"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <Label>Professores (opcional)</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {staffList.map(s => (
                      <button
                        key={s.user_id}
                        type="button"
                        onClick={() => toggleTeacher(s.user_id)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          formData.teachers.includes(s.user_id)
                            ? "bg-[#6697a4] text-white border-[#6697a4]"
                            : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#6697a4]"
                        }`}
                        data-testid={`teacher-toggle-${s.user_id}`}
                      >
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={s.picture} />
                          <AvatarFallback className="text-[8px]">{s.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {s.name}
                        {formData.teachers.includes(s.user_id) && <Check className="h-3 w-3" />}
                      </button>
                    ))}
                    {staffList.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum professor registado</p>}
                  </div>
                </div>
                {formData.lesson_type === "private" && (
                  <div>
                    <Label>Alunos</Label>
                    <p className="text-xs text-[#94A3B8] mb-2">Pesquisa e seleciona os alunos para esta aula privada</p>
                    
                    {/* Selected students chips */}
                    {selectedStudents.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {selectedStudents.map(sid => {
                          const s = studentList.find(st => st.user_id === sid);
                          if (!s) return null;
                          return (
                            <span key={sid} className="inline-flex items-center gap-1 bg-[#6697a4] text-white text-xs px-2.5 py-1 rounded-full">
                              {s.name || s.email}
                              <button type="button" onClick={() => setSelectedStudents(prev => prev.filter(id => id !== sid))} className="hover:bg-white/20 rounded-full p-0.5" data-testid={`remove-student-${sid}`}>
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Search input */}
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        placeholder="Pesquisar por nome, email ou telefone..."
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                        className="pl-9 h-9 text-sm"
                        data-testid="student-search-input"
                      />
                      {studentSearch && (
                        <button type="button" onClick={() => setStudentSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    
                    {/* Filtered results */}
                    <ScrollArea className="max-h-44 border rounded-lg">
                      <div className="p-1">
                        {(() => {
                          const q = studentSearch.toLowerCase().trim();
                          const filtered = q
                            ? studentList.filter(s =>
                                (s.name || "").toLowerCase().includes(q) ||
                                (s.email || "").toLowerCase().includes(q) ||
                                (s.phone || "").replace(/\s/g, "").includes(q.replace(/\s/g, ""))
                              )
                            : studentList.slice(0, 20);
                          
                          if (filtered.length === 0) {
                            return <p className="text-xs text-[#94A3B8] text-center py-4">{q ? "Nenhum aluno encontrado" : "Sem alunos registados"}</p>;
                          }
                          
                          return filtered.map(s => {
                            const isSelected = selectedStudents.includes(s.user_id);
                            return (
                              <button
                                key={s.user_id}
                                type="button"
                                onClick={() => setSelectedStudents(prev => isSelected ? prev.filter(id => id !== s.user_id) : [...prev, s.user_id])}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
                                  isSelected ? "bg-[#6697a4]/10" : "hover:bg-slate-50"
                                }`}
                                data-testid={`student-toggle-${s.user_id}`}
                              >
                                <Avatar className="h-7 w-7 shrink-0">
                                  <AvatarImage src={s.picture} />
                                  <AvatarFallback className="text-[10px] bg-[#6697a4] text-white">{(s.name || s.email || "?").charAt(0).toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-[#0F172A] truncate">{s.name || "Sem nome"}</p>
                                  <p className="text-[11px] text-[#94A3B8] truncate">{s.email}{s.phone ? ` · ${s.phone}` : ""}</p>
                                </div>
                                <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                                  isSelected ? "bg-[#6697a4] border-[#6697a4]" : "border-[#CBD5E1]"
                                }`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </ScrollArea>
                    {!studentSearch && studentList.length > 20 && (
                      <p className="text-[11px] text-[#94A3B8] mt-1">A mostrar 20 de {studentList.length} alunos. Usa a pesquisa para encontrar mais.</p>
                    )}
                  </div>
                )}
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancelar</Button>
                </DialogClose>
                <Button type="submit" className="bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="save-lesson-btn">
                  {editingLesson ? "Guardar" : "Criar Aula"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Auto-Schedule Config (Dialog) */}
      <Dialog open={showAutoConfig} onOpenChange={setShowAutoConfig}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aulas Automáticas</DialogTitle>
          </DialogHeader>
          <AdminSchedule />
        </DialogContent>
      </Dialog>

      {/* Past Lessons: Search + Filters */}
      {showPast && (
        <div className="space-y-3" data-testid="past-lessons-filters">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
              <Input
                placeholder="Pesquisar aulas por titulo..."
                value={pastSearchInput}
                onChange={(e) => handlePastSearchChange(e.target.value)}
                className="pl-10"
                data-testid="past-lessons-search"
              />
              {pastSearchInput && (
                <button onClick={() => handlePastSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowPastFilters(!showPastFilters)} className={`whitespace-nowrap ${showPastFilters ? "bg-[#6697a4]/10 border-[#6697a4]" : ""}`} data-testid="toggle-past-filters">
              <Filter className="h-3.5 w-3.5 mr-1.5" /> Filtros
              {(pastDateFrom || pastDateTo || pastTeacherId || pastCategory) && <Badge className="ml-1.5 bg-[#6697a4] text-white border-0 text-[10px] h-4 w-4 p-0 flex items-center justify-center rounded-full">!</Badge>}
            </Button>
          </div>
          {showPastFilters && (
            <Card className="bg-white border-0 shadow-sm rounded-xl">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs text-[#94A3B8] mb-1 block">Data Inicio</Label>
                    <Input type="date" value={pastDateFrom} onChange={(e) => setPastDateFrom(e.target.value)} data-testid="past-filter-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94A3B8] mb-1 block">Data Fim</Label>
                    <Input type="date" value={pastDateTo} onChange={(e) => setPastDateTo(e.target.value)} data-testid="past-filter-date-to" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94A3B8] mb-1 block">Professor</Label>
                    <Select value={pastTeacherId} onValueChange={setPastTeacherId}>
                      <SelectTrigger data-testid="past-filter-teacher"><SelectValue placeholder="Todos" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_teachers">Todos</SelectItem>
                        {staffList.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#94A3B8] mb-1 block">Categoria</Label>
                    <Select value={pastCategory} onValueChange={setPastCategory}>
                      <SelectTrigger data-testid="past-filter-category"><SelectValue placeholder="Todas" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_types">Todas</SelectItem>
                        <SelectItem value="group">Grupo</SelectItem>
                        <SelectItem value="private">Privada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" className="bg-[#6697a4] hover:bg-[#3d5a63] text-xs" onClick={handlePastFilterApply} data-testid="apply-past-filters">Aplicar</Button>
                  <Button size="sm" variant="outline" className="text-xs" onClick={clearPastFilters} data-testid="clear-past-filters">Limpar</Button>
                  <span className="text-xs text-[#94A3B8] ml-auto">{pastTotal} aula{pastTotal !== 1 ? "s" : ""} encontrada{pastTotal !== 1 ? "s" : ""}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : lessons.length === 0 ? (
        <Card className="bg-white border-0 shadow-sm rounded-xl">
          <CardContent className="p-12 text-center">
            <Waves className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#475569]">Ainda não há aulas</p>
            <p className="text-sm text-[#94A3B8]">Clica em "Nova Aula" para adicionar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {(() => {
            const grouped = {};
            lessons.forEach(l => {
              const d = l.date;
              if (!grouped[d]) grouped[d] = [];
              grouped[d].push(l);
            });
            const todayStr = new Date().toISOString().slice(0, 10);
            const firstActiveFutureId = lessons.find(l => l.status === "active" && l.date >= todayStr)?.lesson_id;
            return Object.entries(grouped).map(([dateKey, dayLessons]) => (
              <div key={dateKey}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">
                    {(() => { try { return format(parseISO(dateKey), "EEEE, d MMM yyyy", { locale: pt }); } catch { return dateKey; } })()}
                  </span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="grid gap-6">
                  {dayLessons.map((lesson) => {
                    const isHighlighted = lesson.lesson_id === firstActiveFutureId;
                    return (
                    <Card key={lesson.lesson_id} ref={expandedLesson === lesson.lesson_id ? expandRef : null} className={`border-0 shadow-sm rounded-xl ${isHighlighted ? "ring-2 ring-[#6697a4]/30 bg-[#6697a4]/5" : "bg-white"}`} data-testid={`admin-lesson-${lesson.lesson_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between cursor-pointer gap-2" onClick={() => toggleExpand(lesson.lesson_id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={lesson.lesson_type === "private" ? "bg-[#FF7F50] text-white border-0" : "bg-[#6697a4] text-white border-0"}>
                        {lesson.lesson_type === "private" ? "Privada" : "Grupo"}
                      </Badge>
                      <Badge variant="outline">{lesson.lesson_category === "surf" ? "Surf" : lesson.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard"}</Badge>
                      <Badge variant="outline" className={lesson.status === "active" ? "border-green-500 text-green-600" : "border-red-500 text-red-600"}>
                        {lesson.status === "active" ? "Ativa" : lesson.status}
                      </Badge>
                      {isHighlighted && <Badge className={`${getLessonStatusColor(getLessonStatus(lesson))} border-0 text-[10px]`}>{getLessonStatusLabel(lesson, true)}</Badge>}
                    </div>
                    <h3 className="text-xl font-bold text-[#3d5a63] mb-2">{lesson.lesson_category === "surf" ? "Surf" : lesson.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard"}</h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#475569]">
                      <span>{format(parseISO(lesson.date), "EEEE, d MMM yyyy", { locale: pt })}</span>
                      <span>{lesson.start_time} - {lesson.end_time}</span>
                      <span className="font-medium">{lesson.current_enrollments}/{lesson.max_students} inscritos</span>
                    </div>
                    {/* Teachers display */}
                    {lesson.teachers_data?.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-[#94A3B8]">Professores:</span>
                        <div className="flex -space-x-2">
                          {lesson.teachers_data.map(t => (
                            <Avatar key={t.user_id} className="h-6 w-6 border-2 border-white" title={t.name}>
                              <AvatarImage src={t.picture} />
                              <AvatarFallback className="text-[8px] bg-[#6697a4] text-white">{t.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                          ))}
                        </div>
                        <span className="text-xs text-[#475569]">{lesson.teachers_data.map(t => t.name).join(", ")}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    {lesson.lesson_type !== "private" && <span className="text-lg sm:text-xl font-bold text-[#6697a4] mr-1">{lesson.price}€</span>}
                    <LessonQRCode lessonId={lesson.lesson_id} lessonTitle={lesson.lesson_category === "surf" ? "Surf" : lesson.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard"} />
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleEdit(lesson); }} data-testid={`edit-lesson-${lesson.lesson_id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); handleDelete(lesson.lesson_id); }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    <ChevronDown className={`h-4 w-4 text-[#94A3B8] transition-transform ${expandedLesson === lesson.lesson_id ? "rotate-180" : ""}`} />
                  </div>
                </div>

                {/* Expanded: Enrolled Students + Forecast */}
                {expandedLesson === lesson.lesson_id && (
                  <div className="mt-4 pt-4 border-t space-y-4" data-testid={`lesson-detail-${lesson.lesson_id}`}>
                    {/* Marine Forecast */}
                    {forecast && !forecast.error && (
                      <div className="flex flex-wrap gap-4 p-3 bg-blue-50/50 rounded-xl text-sm">
                        <span className="font-medium text-[#3d5a63] flex items-center gap-1"><Waves className="h-4 w-4 text-[#6697a4]" /> Previsão marítima:</span>
                        {forecast.wave_height_max != null && <span className="text-[#475569]">Ondas: <strong>{forecast.wave_height_max}m</strong></span>}
                        {forecast.wave_period_max != null && <span className="text-[#475569]">Período: <strong>{forecast.wave_period_max}s</strong></span>}
                        {forecast.wave_direction != null && <span className="text-[#475569]">Direção: <strong>{forecast.wave_direction}°</strong></span>}
                        {forecast.wind_max != null && <span className="text-[#475569]">Vento: <strong>{forecast.wind_max} km/h</strong></span>}
                        {forecast.temp_max != null && <span className="text-[#475569]">Temp: <strong>{forecast.temp_min}° / {forecast.temp_max}°</strong></span>}
                      </div>
                    )}

                    {/* Enrolled Students */}
                    <div>
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h4 className="font-semibold text-[#3d5a63]">Alunos Inscritos ({lessonEnrollments.length})</h4>
                        <div className="flex gap-2">
                          {lesson.status !== "cancelled" && (
                            <>
                              <Button variant="outline" size="sm" className="text-[#6697a4] border-[#6697a4]/30 hover:bg-[#6697a4]/5 h-7 text-xs" onClick={(e) => { e.stopPropagation(); setAddStudentLessonId(lesson.lesson_id); setAddStudentSearch(""); setAddStudentResults([]); }} data-testid={`add-student-btn-${lesson.lesson_id}`}>
                                <UserPlus className="h-3 w-3 mr-1" /> Adicionar Aluno
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleCancelLesson(lesson.lesson_id); }} data-testid={`cancel-lesson-${lesson.lesson_id}`}>
                                <X className="h-3 w-3 mr-1" /> Cancelar Aula
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Add student inline search */}
                      {addStudentLessonId === lesson.lesson_id && (
                        <div className="mb-3 p-3 bg-[#6697a4]/5 rounded-xl border border-[#6697a4]/10" data-testid={`add-student-panel-${lesson.lesson_id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-[#475569]">Adicionar aluno à aula</span>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 text-xs text-[#6697a4]" 
                                onClick={() => setShowAddStudentCreate(!showAddStudentCreate)}
                                data-testid={`toggle-create-student-${lesson.lesson_id}`}
                              >
                                <UserPlus className="h-3 w-3 mr-1" /> Criar Novo
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setAddStudentLessonId(null); setShowAddStudentCreate(false); }}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>

                          {/* Inline create new student */}
                          {showAddStudentCreate && (
                            <div className="mb-3 p-3 bg-white rounded-lg border border-slate-200 space-y-2">
                              <Input 
                                placeholder="Nome *" 
                                value={addNewStudent.name} 
                                onChange={(e) => setAddNewStudent({...addNewStudent, name: e.target.value})} 
                                className="h-8 text-sm" 
                                data-testid={`new-student-name-${lesson.lesson_id}`}
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <Input 
                                  placeholder="Email (opcional)" 
                                  value={addNewStudent.email} 
                                  onChange={(e) => setAddNewStudent({...addNewStudent, email: e.target.value})} 
                                  className="h-8 text-sm" 
                                  data-testid={`new-student-email-${lesson.lesson_id}`}
                                />
                                <Input 
                                  placeholder="Telefone (opcional)" 
                                  value={addNewStudent.phone} 
                                  onChange={(e) => setAddNewStudent({...addNewStudent, phone: e.target.value})} 
                                  className="h-8 text-sm" 
                                  data-testid={`new-student-phone-${lesson.lesson_id}`}
                                />
                              </div>
                              <Button 
                                size="sm" 
                                onClick={handleCreateAndAddStudentToLesson} 
                                disabled={addStudentLoading}
                                className="bg-[#6697a4] hover:bg-[#567f8a] h-7 text-xs" 
                                data-testid={`create-add-student-btn-${lesson.lesson_id}`}
                              >
                                {addStudentLoading ? "A criar..." : "Criar e Adicionar à Aula"}
                              </Button>
                            </div>
                          )}

                          {/* Search existing students */}
                          <div className="relative mb-2">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
                            <Input
                              placeholder="Pesquisar aluno por nome ou email..."
                              value={addStudentSearch}
                              onChange={(e) => setAddStudentSearch(e.target.value)}
                              className="pl-8 h-8 text-sm"
                              data-testid={`add-student-search-${lesson.lesson_id}`}
                            />
                          </div>
                          {addStudentResults.length > 0 && (
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {addStudentResults.map(s => (
                                <button
                                  key={s.user_id}
                                  onClick={() => handleAddStudentToLesson(s.user_id)}
                                  disabled={addStudentLoading}
                                  className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-white transition-colors text-left"
                                  data-testid={`add-student-option-${s.user_id}`}
                                >
                                  <Avatar className="h-7 w-7 shrink-0">
                                    <AvatarImage src={s.picture} />
                                    <AvatarFallback className="bg-[#6697a4] text-white text-[10px]">{s.name?.charAt(0)}</AvatarFallback>
                                  </Avatar>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-[#0F172A] truncate">{s.name || s.email}</p>
                                    <p className="text-[10px] text-[#94A3B8]">{s.credits || 0} créditos grupo · {s.private_credits || 0} privados</p>
                                  </div>
                                  <UserPlus className="h-4 w-4 text-[#6697a4] shrink-0" />
                                </button>
                              ))}
                            </div>
                          )}
                          {addStudentSearch.trim() && addStudentResults.length === 0 && (
                            <p className="text-xs text-[#94A3B8] text-center py-2">Nenhum aluno encontrado</p>
                          )}
                        </div>
                      )}
                      {loadingEnrollments ? (
                        <div className="flex justify-center py-4"><div className="w-6 h-6 border-2 border-[#6697a4] border-t-transparent rounded-full animate-spin" /></div>
                      ) : lessonEnrollments.length === 0 ? (
                        <p className="text-sm text-[#94A3B8] py-2">Nenhum aluno inscrito nesta aula.</p>
                      ) : (
                        <div className="grid gap-2">
                          {lessonEnrollments.map(enrollment => (
                            <div key={enrollment.enrollment_id} className="p-3 bg-slate-50 rounded-xl" data-testid={`enrollment-${enrollment.enrollment_id}`}>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8 shrink-0">
                                  <AvatarImage src={enrollment.user?.picture} />
                                  <AvatarFallback className="bg-[#6697a4] text-white text-xs">{enrollment.user?.name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <Link to={`/students/${enrollment.user_id}`} className="text-sm font-medium text-[#3d5a63] hover:text-[#6697a4] hover:underline truncate" onClick={e => e.stopPropagation()} data-testid={`enrollment-profile-${enrollment.enrollment_id}`}>
                                      {enrollment.user?.name || enrollment.user?.email}
                                    </Link>
                                    <Badge className={`shrink-0 ${
                                      enrollment.status === "attended" ? "bg-green-100 text-green-700 border-0" :
                                      enrollment.status === "absent" ? "bg-red-100 text-red-700 border-0" :
                                      enrollment.status === "reserved" ? "bg-amber-100 text-amber-700 border-0" :
                                      "bg-blue-100 text-blue-700 border-0"
                                    }`}>
                                      {enrollment.status === "attended" ? "Presente" : enrollment.status === "absent" ? "Faltou" : enrollment.status === "reserved" ? "Reservado" : "Inscrito"}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                    {enrollment.user?.ranking_surf && (
                                      <Badge className={`text-[10px] px-1.5 py-0 border ${RANKING_COLORS[enrollment.user.ranking_surf] || ""}`}>
                                        S: {enrollment.user.ranking_surf}
                                      </Badge>
                                    )}
                                    {enrollment.user?.ranking_bodyboard && (
                                      <Badge className={`text-[10px] px-1.5 py-0 border ${RANKING_COLORS[enrollment.user.ranking_bodyboard] || ""}`}>
                                        BB: {enrollment.user.ranking_bodyboard}
                                      </Badge>
                                    )}
                                    {enrollment.activity_type && (
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                        {enrollment.activity_type === "surf" ? "Surf" : "Bodyboard"}
                                      </Badge>
                                    )}
                                    {enrollment.has_subscription && (
                                      <Badge className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700 border-0">
                                        Mensal
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {!["attended", "absent", "cancelled"].includes(enrollment.status) && (
                                <div className="flex items-center gap-2 flex-wrap mt-2 ml-11">
                                  <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50 h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleMarkAttendance(enrollment.enrollment_id); }} data-testid={`mark-attendance-${enrollment.enrollment_id}`}>
                                    <Check className="h-3 w-3 mr-1" /> Presente
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleMarkAbsent(enrollment.enrollment_id, false); }} data-testid={`mark-absent-${enrollment.enrollment_id}`}>
                                    <X className="h-3 w-3 mr-1" /> Faltou
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-amber-600 hover:bg-amber-50 h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleMarkAbsent(enrollment.enrollment_id, true); }} data-testid={`mark-absent-refund-${enrollment.enrollment_id}`}>
                                    Faltou + Devolver
                                  </Button>
                                </div>
                              )}
                              {["attended", "confirmed", "absent"].includes(enrollment.status) && (
                                <div className="flex items-center gap-2 mt-2 ml-11">
                                  <Button size="sm" variant="ghost" className="text-[#6697a4] hover:bg-[#6697a4]/10 h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleRevertAttendance(enrollment.enrollment_id); }} data-testid={`revert-attendance-${enrollment.enrollment_id}`}>
                                    <Undo2 className="h-3 w-3 mr-1" /> Reverter
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* Past Lessons Pagination */}
      {showPast && !loading && pastPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2" data-testid="past-lessons-pagination">
          <Button variant="outline" size="sm" disabled={pastPage <= 1} onClick={() => handlePastPageChange(pastPage - 1)} className="h-8 px-3" data-testid="past-prev-page">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(pastPages, 5) }, (_, i) => {
              let p;
              if (pastPages <= 5) { p = i + 1; }
              else if (pastPage <= 3) { p = i + 1; }
              else if (pastPage >= pastPages - 2) { p = pastPages - 4 + i; }
              else { p = pastPage - 2 + i; }
              return (
                <Button key={p} variant={p === pastPage ? "default" : "outline"} size="sm" className={`h-8 w-8 p-0 ${p === pastPage ? "bg-[#6697a4] text-white" : ""}`} onClick={() => handlePastPageChange(p)}>{p}</Button>
              );
            })}
          </div>
          <Button variant="outline" size="sm" disabled={pastPage >= pastPages} onClick={() => handlePastPageChange(pastPage + 1)} className="h-8 px-3" data-testid="past-next-page">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-xs text-[#94A3B8] ml-2 hidden sm:inline">{pastTotal} aula{pastTotal !== 1 ? "s" : ""}</span>
        </div>
      )}
    </div>
  );
};

// Admin Enrollments Component

// Admin Schedule is now imported from components/admin/AdminSchedule.jsx

export default AdminLessons;
